import { Component, computed, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, skip, take } from 'rxjs';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { FormDirective } from '../../../../shared/directives/form.directive';
import { MmrService } from '../../../mmr/services/mmr.service';
import { ClaimFormStep, ClaimFormStepsEnum } from './claim-form.types';
import { ClaimTypeSelectionComponent } from './components/claim-type-selection/claim-type-selection.component';
import { ContactDetailsComponent } from './components/contact-details/contact-details.component';
import { GenericFormHandlerComponent } from './components/generic-form-handler/generic-form-handler.component';
import { ModelNumberCaptureComponent } from './components/model-number-capture/model-number-capture.component';
import { ServiceOptionsComponent } from './components/service-options/service-options.component';
import { VisitAppointmentComponent } from './components/visit-appointment/visit-appointment.component';
import { ClaimsFormService } from './services/claims-form.service';

@Component({
  selector: 'app-claims-form',
  standalone: true,
  imports: [
    ButtonComponent,
    FormsModule,
    ClaimTypeSelectionComponent,
    GenericFormHandlerComponent,
    FormDirective,
    ModelNumberCaptureComponent,
    ContactDetailsComponent,
    ServiceOptionsComponent,
    VisitAppointmentComponent,
  ],
  templateUrl: './claims-form.component.html',
  styleUrl: './claims-form.component.scss',
})
export class ClaimsFormComponent implements OnInit {
  currentStep = signal<ClaimFormStep | undefined>(undefined);
  planNumber = signal<string | undefined>(undefined);

  stageSubmitted = signal<boolean>(false);

  formValid = signal(false);

  protected formValue = signal<Partial<Record<ClaimFormStep, any>>>({});

  loadingData = signal<boolean>(false);

  private viewModel = computed(() => {
    const { plan } = this.mmrService.planClaimsDetails;
    const claimsFormState = this.claimsFormService.state;
    return {
      formLoading: claimsFormState.isLoading,
      submittingValue: claimsFormState.submittingValue,
      currentClaimSteps: claimsFormState.currentClaimSteps,
      activeStep: claimsFormState.activeStep,
      stageSubmitted: this.stageSubmitted(),
      formValue: this.formValue(),
      planNumber: this.planNumber(),
      currentStep: this.currentStep() as ClaimFormStep,
      backButtonGoesToMMRPage: this.currentStep() === ClaimFormStepsEnum.ClaimType,
      mmrPageLink: ['/', this.planNumber() as string, 'claims'],
      formValid: this.formValid(),
      backButtonIsLink: claimsFormState.currentSubStep === 0,
      stepConfig: claimsFormState.stepConfig,
      loadingData: this.loadingData(),
      productDescription: plan.value()?.description,
    };
  });

  get vm() {
    return this.viewModel();
  }

  stepTitle: Record<string, string> = {
    'claim-type': 'What happened?',
    item_manufacturer: 'Who makes your product?',
    item_type: 'What kind of product is it?',
    item_product: 'Find your model number',
    assessment_fault_code: 'How is it faulty?',
    assessment_working: 'Is your product still usable?',
    fault_date: 'When did the fault start?',
    contact_details: 'Check your details',
    service_option: 'What type of appointment do you want?',
    visit_appointment: 'Pick your repair date',
  };

  buttonTitle: Record<string, string> = {
    contact_details: 'Pick a repair date',
    visit_appointment: 'Book this time',
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly mmrService: MmrService,
    private readonly claimsFormService: ClaimsFormService
  ) {}

  ngOnInit() {
    this.planNumber.set(this.route.snapshot.params['planNumber']);
    this.claimsFormService.setPlanNumber(this.planNumber() as string);
    this.listenToStepChanges();

    this.getStoredFormValue();
  }

  getStoredFormValue(): void {
    this.claimsFormService.initialFormValue
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe({
        next: (val) => {
          this.formValue.set(val);
        },
      });
  }

  listenToStepChanges(): void {
    this.currentStep.set(this.route.snapshot.params['step']);
    this.route.params.pipe(skip(1)).subscribe({
      next: ({ step }) => {
        this.stageSubmitted.set(false);
        this.currentStep.set(step);

        if (step === 'claim-type') {
          this.claimsFormService.reset();
        }
      },
    });
  }

  setFormValidStatus(formValid: boolean): void {
    this.formValid.set(formValid);
  }

  getSubmitValue(): any {
    if (this.vm.currentStep === ClaimFormStepsEnum.ClaimType) {
      return this.vm.formValue[ClaimFormStepsEnum.ClaimType]['claim-type'];
    }

    return this.vm.formValue[this.vm.currentStep!];
  }

  goBack(): void {
    this.claimsFormService.goToPreviousStep();
    this.getStoredFormValue();
  }

  submitFormValue(): void {
    this.stageSubmitted.set(true);

    if (!this.vm.formValid) {
      return;
    }

    if (this.vm.currentStep === ClaimFormStepsEnum.ClaimType) {
      this.claimsFormService.startClaim(this.getSubmitValue()).subscribe({
        error: () => {
          this.stageSubmitted.set(false);
          this.router.navigate(this.vm.mmrPageLink);
        },
      });
      return;
    }

    this.claimsFormService.storeFormValue(
      this.vm.currentStep as ClaimFormStep,
      this.vm.formValue[this.vm.currentStep!]
    );

    this.claimsFormService.goToNextStep().subscribe({
      next: () => {
        this.getStoredFormValue();
        this.stageSubmitted.set(false);
      },
      error: () => {
        this.stageSubmitted.set(false);
        this.router.navigate(this.vm.mmrPageLink);
      },
    });
  }
}
